import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { states, type StateStatus } from '@/lib/seo/states'

export const Route = createFileRoute('/online-poker/$state/')({
  loader: ({ params }) => {
    const state = states.find((s) => s.slug === params.state)
    if (!state) throw notFound()
    return state
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    return {
      meta: [
        { title: `Online Poker in ${loaderData.name} | Powka` },
        {
          name: 'description',
          content: `Online poker laws and regulations in ${loaderData.name}. Is online poker legal? Find out the current status and key facts.`,
        },
        {
          property: 'og:title',
          content: `Online Poker in ${loaderData.name} | Powka`,
        },
        {
          property: 'og:description',
          content: `Online poker laws and regulations in ${loaderData.name}.`,
        },
        { property: 'og:type', content: 'article' },
      ],
    }
  },
  component: RouteComponent,
})

const statusConfig: Record<StateStatus, { label: string; className: string }> =
  {
    legal: {
      label: 'Legal & Regulated',
      className:
        'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20',
    },
    'gray-area': {
      label: 'Unregulated',
      className:
        'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20',
    },
    illegal: {
      label: 'Prohibited',
      className:
        'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20',
    },
  }

function RouteComponent() {
  const state = Route.useLoaderData()
  const status = statusConfig[state.status]

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span>/</span>
        <Link to="/online-poker" className="hover:text-foreground">
          Online Poker
        </Link>
        <span>/</span>
        <span className="text-foreground">{state.name}</span>
      </nav>

      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-3xl font-bold">Online Poker in {state.name}</h1>
      </div>

      <span
        className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-8 ${status.className}`}
      >
        {status.label}
      </span>

      <p className="text-muted-foreground leading-relaxed mb-10">
        {state.summary}
      </p>

      <div className="mb-12">
        <h2 className="text-lg font-semibold mb-4">Key Facts</h2>
        <ul className="space-y-3">
          {state.keyFacts.map((fact, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
              <span className="text-muted-foreground">{fact}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-12">
        <h2 className="text-lg font-semibold mb-4">
          Online Poker by City in {state.name}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {state.cities.map((city) => (
            <Link
              key={city.slug}
              to="/online-poker/$state/$city"
              params={{ state: state.slug, city: city.slug }}
              className="rounded-md border px-4 py-3 text-sm font-medium hover:bg-secondary/50 transition-colors"
            >
              {city.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-secondary/30 px-6 py-5">
        <h2 className="font-semibold mb-1">Play on Powka</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Powka is a no-limit Texas Hold'em platform with a 1% rake. Deposits
          and withdrawals are handled in USDC.
        </p>
        <Link
          to="/"
          className="inline-block rounded-md bg-foreground text-background text-sm font-medium px-4 py-2 hover:opacity-90 transition-opacity"
        >
          Start playing
        </Link>
      </div>

      <div className="mt-10">
        <Link
          to="/online-poker"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← View all states
        </Link>
      </div>
    </div>
  )
}
